/**
 * One parser for every push the site can receive:
 *   - V2 (FCM): flat string `data` per docs/engine-contracts/notifications-v2-client-contract.md
 *   - V1 (OneSignal `additionalData`): `{ type, id | screen | url, notification_type, notification_group }`,
 *     which the contract keeps parseable during the migration
 * Both become the same internal shape; invalid input returns `null`, never throws.
 */
export const ACTION_TYPES = ['none', 'promo', 'content', 'screen', 'url'];
const MAX_PARAMS_BYTES = 1024;

const str = value =>
  typeof value === 'string' ? value : value == null ? '' : String(value);

const parseParams = raw => {
  if (!raw || raw === '{}') {
    return null;
  }
  if (typeof raw === 'object') {
    return raw;
  }
  if (typeof raw !== 'string' || raw.length > MAX_PARAMS_BYTES) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed
      : null;
  } catch {
    return null;
  }
};

const isObjectId = value => /^[a-f0-9]{24}$/.test(str(value));

/** V2 data payload → internal notification. */
export const parseV2 = data => {
  if (!data || typeof data !== 'object' || str(data.schema_version) !== '2') {
    return null;
  }
  const notificationId = str(data.notification_id);
  const deliveryId = str(data.delivery_id);
  if (!isObjectId(notificationId) || !isObjectId(deliveryId)) {
    return null;
  }
  const actionType = ACTION_TYPES.includes(data.action_type)
    ? data.action_type
    : 'none';
  const target = str(data.action_target);

  return {
    version: '2',
    notification_id: notificationId,
    delivery_id: deliveryId,
    campaign_id: str(data.campaign_id) || null,
    category: str(data.category) || 'marketing',
    action: {
      type: actionType,
      target: actionType === 'none' ? null : target || null,
      params: parseParams(data.action_params),
    },
    receipt_token: str(data.receipt_token) || null,
    analytics_label: str(data.analytics_label) || null,
  };
};

/** V1 OneSignal additionalData → internal notification (no delivery, no receipt). */
export const parseV1 = data => {
  if (!data || typeof data !== 'object' || !data.type) {
    return null;
  }
  const actionType = ACTION_TYPES.includes(data.type) ? data.type : 'none';
  const target =
    actionType === 'screen'
      ? str(data.screen)
      : actionType === 'url'
      ? str(data.url)
      : str(data.id);

  return {
    version: '1',
    notification_id: str(data.notification_group) || null,
    delivery_id: null,
    campaign_id: str(data.notification_group) || null,
    category: str(data.notification_type) || 'marketing',
    action: {
      type: actionType,
      target: actionType === 'none' ? null : target || null,
      params: null,
    },
    receipt_token: null,
    analytics_label: null,
  };
};

/** Anything a listener hands us: an FCM remote message, a data object, or V1 additionalData. */
export const parsePayload = input => {
  if (!input || typeof input !== 'object') {
    return null;
  }
  const data =
    input.data && typeof input.data === 'object' ? input.data : input;
  return parseV2(data) || parseV1(data);
};

/**
 * The shape the existing router (`components/PushNotifications`) navigates with:
 * `{ type, id | screen | url, ...params }`.
 */
export const toRouteData = notification => {
  const { action } = notification;
  const base = {
    type: action.type,
    notification_id: notification.notification_id,
  };

  switch (action.type) {
    case 'promo':
    case 'content':
      return { ...base, id: action.target };
    case 'screen':
      return { ...base, screen: action.target, ...(action.params || {}) };
    case 'url':
      return { ...base, url: action.target };
    default:
      return base;
  }
};
